import type { ApprovalChatEntry, Avatar, ChatEntry, RosterAgent, ServerEvent, ToolResult } from "@ark-bot/protocol";

import { locale as appLanguage, type Locale } from "./i18n.js";
import { TOUR_SCRIPT } from "./text/tour-script.js";

/**
 * The first-run tour (components/Tour.tsx): when it opens by itself, and the
 * practice bot's scripted turns. The practice renders through the same
 * reducer as a real chat (state.ts applyEvent), so what the user tries in the
 * tour looks exactly like what they will see afterwards; nothing in it
 * reaches the host.
 *
 * Its words follow the app's language, so an Indonesian user practises in
 * Indonesian and their first real message to the Bot Hiring Manager is in
 * Indonesian too. The tour plays the practice in both languages at once and
 * shows the one the app is in, so a language picked mid-tour changes what
 * was already said as well as what comes next.
 */

/** The script's lines in one language, or in the app's language now. */
const lines = (language?: Locale): typeof TOUR_SCRIPT.en => TOUR_SCRIPT[language ?? appLanguage()];

/** What the page should do about the tour, once it knows enough to say. */
export type TourDecision = "wait" | "show" | "skip" | "settle";

export interface TourInput {
  /**
   * The host's settings as read; "failed" when they could not be. Their
   * `onboarded` flag is the only record of the tour: it belongs to the
   * workspace, so a fresh box has none whichever browser opens it.
   */
  readonly settings: "loading" | "failed" | { readonly onboarded?: boolean | undefined };
  /** The roster's first full snapshot; null until it has arrived. */
  readonly roster: readonly RosterAgent[] | null;
}

/**
 * A workspace nobody has talked in yet, which is what a new one is: it holds
 * only the bots it was seeded with, none of whom has said anything. What is
 * done once for a new workspace (the tour, its zone) asks this.
 */
export function isNewWorkspace(roster: readonly RosterAgent[]): boolean {
  return roster.every((agent) => agent.lastEntry == null);
}

/**
 * The tour opens once, on a new workspace. A workspace with a conversation
 * in it is not new however the flag reads (the prototype's roster veto), so
 * the flag is settled without showing anything. Settings that cannot be read
 * are no reason to put a tour in front of anyone.
 */
export function tourDecision({ settings, roster }: TourInput): TourDecision {
  if (settings === "failed") return "skip";
  if (settings !== "loading" && settings.onboarded === true) return "skip";
  if (settings === "loading" || roster == null) return "wait";
  return isNewWorkspace(roster) ? "show" : "settle";
}

/* --- the practice bot ------------------------------------------------------ */

export const PRACTICE_ID = "practice-bot";
export const PRACTICE_NAME = "Scout";
export const PRACTICE_AVATAR: Avatar = { kind: "mark", shape: "hexagon", color: "#4dd0c1", label: "S" };
const SESSION = "practice";

/** One thing the practice bot can be asked, with how it answers. */
export interface PracticeTopic {
  readonly id: string;
  /** The suggestion as the user sends it. */
  readonly ask: string;
  readonly ack: string;
  /** The work it does in between, as tool calls: what the status line and Full conversation show. */
  readonly work: readonly { readonly name: string; readonly args: Record<string, unknown>; readonly result: string; readonly ms: number }[];
  readonly answer: string;
  /** What the email it offers to send would be called. */
  readonly subject: string;
}

/** Each suggestion's words, by its id, for topicIn. */
const SUGGESTED = new Map<string, (say: typeof TOUR_SCRIPT.en) => Omit<PracticeTopic, "id">>();

/**
 * A suggestion whose words are read in the app's language each time they are
 * read, so a language picked mid-tour carries on in it.
 */
function suggestion(id: string, words: (say: typeof TOUR_SCRIPT.en) => Omit<PracticeTopic, "id">): PracticeTopic {
  SUGGESTED.set(id, words);
  const now = (): Omit<PracticeTopic, "id"> => words(lines());
  return {
    id,
    get ask() {
      return now().ask;
    },
    get ack() {
      return now().ack;
    },
    get work() {
      return now().work;
    },
    get answer() {
      return now().answer;
    },
    get subject() {
      return now().subject;
    },
  };
}

/** The suggestions are everyday Indonesian errands: the market the app is made for first. */
export const PRACTICE_TOPICS: readonly PracticeTopic[] = [
  suggestion("saturday", (say) => ({
    ask: say.saturdayAsk,
    ack: say.saturdayAck,
    work: [
      { name: "web_search", args: { query: say.saturdayQuery }, result: say.saturdayFound, ms: 1700 },
      { name: "web_fetch", args: { url: "https://www.indonesia.travel/" }, result: say.saturdayRead, ms: 1400 },
    ],
    answer: say.saturdayAnswer,
    subject: say.saturdaySubject,
  })),
  // The write tool's own report stays as that tool words it, in English.
  suggestion("invoice", (say) => ({
    ask: say.invoiceAsk,
    ack: say.invoiceAck,
    work: [{ name: "write", args: { path: say.invoicePath }, result: `Wrote 640 bytes to ${say.invoicePath}`, ms: 1600 }],
    answer: say.invoiceAnswer(say.invoicePath),
    subject: say.invoiceSubject,
  })),
  suggestion("wallets", (say) => ({
    ask: say.walletsAsk,
    ack: say.walletsAck,
    work: [
      { name: "web_search", args: { query: "GoPay vs OVO vs DANA" }, result: say.walletsFound, ms: 1700 },
      { name: "write", args: { path: say.walletsPath }, result: `Wrote 1.1 KB to ${say.walletsPath}`, ms: 1200 },
    ],
    answer: say.walletsAnswer,
    subject: say.walletsSubject,
  })),
];

/** Something the user wrote in their own words: the practice bot says plainly what a real one would do with it. */
export function ownTopic(text: string, language?: Locale): PracticeTopic {
  const short = text.length > 80 ? `${text.slice(0, 77)}…` : text;
  const say = lines(language);
  return {
    id: "own",
    ask: text,
    ack: say.ownAck,
    work: [{ name: "web_search", args: { query: short }, result: say.ownFound, ms: 1800 }],
    answer: say.ownAnswer(short),
    subject: short,
  };
}

/** A topic's words in one language: a suggestion's own, or the user's own text answered in that language. */
export function topicIn(topic: PracticeTopic, language: Locale): PracticeTopic {
  const words = SUGGESTED.get(topic.id);
  return words == null ? ownTopic(topic.ask, language) : { id: topic.id, ...words(lines(language)) };
}

/** The suggestion the user sent, in whichever language it was shown; null for words of their own. */
export function suggestionFor(text: string): PracticeTopic | null {
  return PRACTICE_TOPICS.find((one) => (["en", "id"] as const).some((language) => topicIn(one, language).ask === text)) ?? null;
}

/**
 * The Chief of Staff's lesson: the user just says what they need, and it
 * works out who should do it. On a fresh workspace nobody fits, so it briefs
 * the Bot Hiring Manager, which checks with the user before making anything.
 */
export interface Delegation {
  readonly ask: string;
  readonly ack: string;
  /** What the chief writes to the hiring manager. */
  readonly brief: string;
  readonly reply: string;
  readonly done: string;
}

/** The example request for the Chief of Staff, in English; the tour offers chiefExample(), in the app's language. */
export const CHIEF_EXAMPLE = TOUR_SCRIPT.en.chiefExample;

/** The example request for the Chief of Staff, in the app's language. */
export function chiefExample(): string {
  return lines().chiefExample;
}

export function delegation(text: string, manager: string, language?: Locale): Delegation {
  // The example in either language, so one sent just before the language changed is still briefed as the example.
  const example = [TOUR_SCRIPT.en, TOUR_SCRIPT.id].some((say) => say.chiefExample === text);
  const short = text.length > 80 ? `${text.slice(0, 77)}…` : text;
  const say = lines(language);
  return {
    // The example is asked in the lesson's language; words of the user's own stay theirs.
    ask: example ? say.chiefExample : text,
    ack: say.chiefAck,
    brief: example ? say.chiefBriefExample : say.chiefBrief(short),
    reply: say.managerReply,
    done: example ? say.chiefDoneExample(manager) : say.chiefDone(manager),
  };
}

/** The Chief of Staff every instance is seeded with, found by its label as the hiring manager is; null when there is none. */
export function chiefOfStaff(agents: readonly RosterAgent[]): RosterAgent | null {
  const bots = agents.filter((agent) => agent.kind === "agent");
  const matches = (value: string | undefined): boolean => value != null && /\bchief of staff\b/i.test(value);
  return bots.find((agent) => matches(agent.config.title)) ?? bots.find((agent) => matches(agent.name)) ?? null;
}

/** The practice bot's opening line, as a real bot says hello when it is made, in English; practiceHello() gives it in the app's language. */
export const PRACTICE_HELLO = TOUR_SCRIPT.en.hello;

/** The practice bot's opening line in the app's language. */
export function practiceHello(): string {
  return lines().hello;
}

/** What the practice bot is labelled, in the app's language. */
export function practiceTitle(): string {
  return lines().practiceTitle;
}

/** One step of a scripted turn: wait this long, then apply these events. */
export interface Beat {
  readonly wait: number;
  readonly events: readonly ServerEvent[];
}

const USAGE = { inputTokens: 5_200, outputTokens: 180 };
/** A tool's result. One standing for what a real tool reports to its bot ("Delivered.") stays in English, as the real one is. */
const output = (value: string): ToolResult => ({ parts: [{ type: "text", text: value }] });

/**
 * Numbers the practice's entries, calls and turns, so every event it makes
 * is one the reducer would accept from the host. Times are stamped when an
 * event is applied, not when it is written, so a bubble reads "now".
 */
export class PracticeScript {
  /**
   * The language this script speaks: fixed, for the tour's two scripts played
   * side by side; the app's language as each line is written when none is
   * given.
   */
  constructor(readonly language?: Locale) {}

  private get words(): typeof TOUR_SCRIPT.en {
    return lines(this.language);
  }

  /** A topic in this script's language. */
  private topic(topic: PracticeTopic): PracticeTopic {
    return this.language == null ? topic : topicIn(topic, this.language);
  }

  private entries = 0;
  private calls = 0;
  private turns = 0;

  /** A bubble, card or note, with its id and place; `createdAt` is filled in as it lands. */
  entry(input: DistributiveOmit<ChatEntry, "id" | "seq" | "createdAt">): ChatEntry {
    const seq = this.entries++;
    return { ...input, id: `${input.kind[0]}${seq}`, seq, createdAt: 0 } as ChatEntry;
  }

  /** The bot saying one thing: typing, then the bubble. */
  say(content: string, wait = 500, typing = 1100): Beat[] {
    const callId = `c${++this.calls}`;
    return [
      { wait, events: [{ type: "tool.started", sessionId: SESSION, callId, name: "send_message", args: { type: "text", content } }] },
      { wait: typing, events: [{ type: "chat.entry", entry: this.entry({ kind: "agent", text: content }) }, { type: "tool.finished", sessionId: SESSION, callId, result: output("Delivered.") }] },
    ];
  }

  /** A tool call that runs for a while and comes back. */
  tool(name: string, args: Record<string, unknown>, result: string, ms: number, wait = 350): Beat[] {
    const callId = `c${++this.calls}`;
    return [
      { wait, events: [{ type: "tool.started", sessionId: SESSION, callId, name, args }] },
      { wait: ms, events: [{ type: "tool.finished", sessionId: SESSION, callId, result: output(result) }] },
    ];
  }

  /** The user's message reaching the bot: the bubble, and the turn it opens. */
  user(message: string): { readonly turnId: string; readonly beats: Beat[] } {
    const turnId = `t${++this.turns}`;
    return {
      turnId,
      beats: [{ wait: 0, events: [{ type: "chat.entry", entry: this.entry({ kind: "user", text: message, attachments: [] }) }, { type: "turn.started", turnId, sessionId: SESSION }, { type: "user.message", sessionId: SESSION, turnId, text: message }] }],
    };
  }

  /** A turn the bot starts on its own, carrying on from the last one. */
  resume(): { readonly turnId: string; readonly beats: Beat[] } {
    const turnId = `t${++this.turns}`;
    return { turnId, beats: [{ wait: 0, events: [{ type: "turn.started", turnId, sessionId: SESSION }] }] };
  }

  finish(turnId: string, wait = 250): Beat {
    return { wait, events: [{ type: "turn.finished", turnId, usage: USAGE }] };
  }

  /** The practice opening: the bot has said hello. */
  hello(): Beat[] {
    return [{ wait: 0, events: [{ type: "chat.entry", entry: this.entry({ kind: "agent", text: this.words.hello }) }] }];
  }

  /** The first task: acknowledged, worked on, answered. */
  task(asked: PracticeTopic): Beat[] {
    const topic = this.topic(asked);
    const { turnId, beats } = this.user(topic.ask);
    return [...beats, ...this.say(topic.ack, 600, 1000), ...topic.work.flatMap((step) => this.tool(step.name, step.args, step.result, step.ms)), ...this.say(topic.answer, 400, 1500), this.finish(turnId)];
  }

  /**
   * The Chief of Staff delegating: it takes the request, writes to the hiring
   * manager (the card both chats show), hears back, and tells the user where
   * things stand. `manager` is who the card names, the real one's id and name
   * where there is one, so its face is the one in the user's list.
   */
  delegate(lesson: Delegation, manager: { readonly id: string; readonly name: string }): Beat[] {
    const { turnId, beats } = this.user(lesson.ask);
    const callId = `c${++this.calls}`;
    return [
      ...beats,
      ...this.say(lesson.ack, 500, 800),
      { wait: 400, events: [{ type: "tool.started", sessionId: SESSION, callId, name: "send_to_agent", args: { agent: manager.name, message: lesson.brief } }] },
      { wait: 1300, events: [{ type: "chat.entry", entry: this.entry({ kind: "peer", direction: "out", peer: manager, text: lesson.brief }) }, { type: "tool.finished", sessionId: SESSION, callId, result: output(`Sent to ${manager.name}.`) }] },
      { wait: 1600, events: [{ type: "chat.entry", entry: this.entry({ kind: "peer", direction: "in", peer: manager, text: lesson.reply }) }] },
      ...this.say(lesson.done, 500, 1300),
      this.finish(turnId),
    ];
  }

  /** A message while the bot is at work: it reaches the turn in flight, which takes it on board. */
  aside(message: string, busy: boolean): Beat[] {
    const { turnId, beats } = this.user(message);
    const say = this.words;
    if (busy) return [beats[0]!, ...this.say(say.asideBusy, 300, 700)];
    return [...beats, ...this.say(say.asideIdle, 500, 1000), this.finish(turnId)];
  }

  /**
   * The bot offering to act, and the action held for the user: its email
   * send is flagged, and the call waits on the card. `approvalId` and
   * `callId` are what the answer settles.
   */
  offer(asked: PracticeTopic): { readonly turnId: string; readonly approvalId: string; readonly callId: string; readonly beats: Beat[] } {
    const topic = this.topic(asked);
    const { turnId, beats } = this.resume();
    const callId = `c${++this.calls}`;
    const approvalId = `ap${this.calls}`;
    const say = this.words;
    const detail = say.offerDetail(say.offerTo, topic.subject);
    const summary = say.offerSummary;
    const reason = say.offerReason;
    return {
      turnId,
      approvalId,
      callId,
      beats: [
        ...beats,
        ...this.say(say.offer, 400, 900),
        { wait: 500, events: [{ type: "tool.started", sessionId: SESSION, callId, name: "gmail_send_email", args: { to: say.offerTo, subject: topic.subject } }] },
        {
          wait: 700,
          events: [
            { type: "approval.requested", approvalId, sessionId: SESSION, callId, tool: "gmail_send_email", reason, summary, detail },
            { type: "chat.entry", entry: this.entry({ kind: "approval", approvalId, tool: "gmail_send_email", summary, reason, detail, status: "pending" }) },
          ],
        },
      ],
    };
  }

  /** The user's answer to the card, and what the bot does with it. */
  answer(card: ApprovalChatEntry, callId: string, turnId: string, approved: boolean): Beat[] {
    const status = approved ? "approved" : "denied";
    const say = this.words;
    return [
      { wait: 0, events: [{ type: "approval.resolved", approvalId: card.approvalId, status }, { type: "chat.entry.updated", entry: { ...card, status } }] },
      { wait: approved ? 900 : 300, events: [{ type: "tool.finished", sessionId: SESSION, callId, result: approved ? output(say.sentResult) : { ...output("The user declined this action."), isError: true } }] },
      ...this.say(approved ? say.sent : say.declined, 300, 900),
      this.finish(turnId),
    ];
  }
}

/** Omit, one member of a union at a time, so each keeps its own fields. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Every event with its bubble dated `at`: what the host would have stamped. */
export function stamp(event: ServerEvent, at: number): ServerEvent {
  if (event.type === "chat.entry" || event.type === "chat.entry.updated") return event.entry.createdAt === 0 ? { ...event, entry: { ...event.entry, createdAt: at } } : event;
  return event;
}

/* --- the first real bot, hired ---------------------------------------------- */

/**
 * The tour ends by handing the user to the Bot Hiring Manager every instance
 * is seeded with (core's DEFAULT_AGENTS): they say what the bot is for, and
 * it asks its few questions and makes the bot, the way bots are made here
 * from then on. Found by its label, which is its job, so a renamed one is
 * still found; by its name failing that.
 */
export function hiringManager(agents: readonly RosterAgent[]): RosterAgent | null {
  const bots = agents.filter((agent) => agent.kind === "agent");
  const matches = (value: string | undefined): boolean => value != null && /\bhiring manager\b/i.test(value);
  return bots.find((agent) => matches(agent.config.title)) ?? bots.find((agent) => matches(agent.name)) ?? null;
}

/** A job to start from: one sentence, as the user might say it. */
export interface BotIdea {
  readonly name: string;
  readonly title: string;
  readonly pitch: string;
  readonly avatar: Avatar;
}

/** The jobs to start from, in the app's language: the pitch goes into the user's first message. */
export function botIdeas(): readonly BotIdea[] {
  const say = lines();
  return [
    { name: say.researcherName, title: say.researcherTitle, pitch: say.researcherPitch, avatar: { kind: "mark", shape: "circle", color: "#6ea8fe", label: say.researcherLabel } },
    { name: say.assistantName, title: say.assistantTitle, pitch: say.assistantPitch, avatar: { kind: "mark", shape: "rounded", color: "#e879a6", label: say.assistantLabel } },
    { name: say.plannerName, title: say.plannerTitle, pitch: say.plannerPitch, avatar: { kind: "mark", shape: "oval", color: "#f5b041", label: say.plannerLabel } },
    { name: say.lookoutName, title: say.lookoutTitle, pitch: say.lookoutPitch, avatar: { kind: "mark", shape: "hexagon", color: "#58d68d", label: say.lookoutLabel } },
    { name: say.writerName, title: say.writerTitle, pitch: say.writerPitch, avatar: { kind: "mark", shape: "star", color: "#a78bfa", label: say.writerLabel } },
  ];
}

/** Where the job starts, when the user practised on something: that task, as the kind of work the bot is for. */
export function pitchFrom(task: string | null): string {
  return task == null ? "" : lines().pitchFrom(task);
}

/**
 * The user's first words to the Bot Hiring Manager: the job, and that this is
 * their first bot, so it knows to walk them through. It is a real message in
 * their chat, so it is in the app's language and the conversation starts in it.
 */
export function hiringMessage(job: string): string {
  return lines().hiringMessage(job.trim());
}

